import '@ant-design/v5-patch-for-react-19';
import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import i18n from 'i18next';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { Toaster } from 'react-hot-toast';
import { initReactI18next } from 'react-i18next';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { PersistGate } from 'redux-persist/integration/react';
import config from '../config.json';
import App from './App.tsx';
import initializeDetectedLanguage from './helpers/initializeLanguage.ts';
import './index.css';
import arCommon from './locales/ar/common.json';
import enCommon from './locales/en/common.json';
import { persistor, store } from './redux/store.ts';

Sentry.init({
  dsn: config.SENTRY_DSN,
});

i18n.use(initReactI18next).init({
  fallbackLng: 'en',
  saveMissing: true,
  missingKeyHandler: (lng, ns, key) => {
    console.warn(`Missing translation: ${key} in ${lng} (${ns})`);
  },
  debug: import.meta.env.NODE_ENV === 'development',
  supportedLngs: ['en', 'ar'],
  resources: {
    en: {
      common: enCommon,
    },
    ar: {
      common: arCommon,
    },
  },
  ns: ['common'],
  defaultNS: 'common',
  interpolation: {
    escapeValue: false,
  },
});

// detect language and set it
const detectedLanguage = initializeDetectedLanguage();
const isArabic = detectedLanguage === 'ar';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: false,
    },
  },
});

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Failed to find the root element');
ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Provider store={store}>
          <PersistGate loading={null} persistor={persistor}>
            <App />
          </PersistGate>
        </Provider>
        {/* <ReactQueryDevtools initialIsOpen={false} /> */}
      </BrowserRouter>
    </QueryClientProvider>
    <Toaster
      toastOptions={{
        duration: 5000,
        style: {
          direction: isArabic ? 'rtl' : 'ltr',
        },
      }}
    />
  </React.StrictMode>
);
